import { Input, Select, Switch } from "antd";
import { useEffect, useState } from "react";
import { useAppSelector } from "../../../../app/store";
import SearchIcon from "../../../../assets/icons/SearchIcon";
import SelectDownIcon from "../../../../assets/icons/SelectDownIcon";
import SortIcon from "../../../../assets/icons/SortIcon";
import ConfirmModal from "../../../../components/ui/ConfirmModal";
import CustomTable from "../../../../components/ui/CustomTable";
import { useGetDepartmentsQuery } from "../../../../features/department/departmentApi";
import {
  useAddEmployeeLiveTrackingSettingMutation,
  useGetTrackingEmployeeListQuery,
  useGetTrackingSettingQuery,
  useUpdateTrackingSettingMutation,
} from "../../../../features/tracking/trackingApi";
import useError from "../../../../hooks/useError";
import useModal from "../../../../hooks/useModal";
import { useQueryParams } from "../../../../hooks/useQueryParams";
import useTable from "../../../../hooks/useTable";
import SidebarLayout from "../../../../layouts/SidebarLayout";
import { toast } from "../../../../lib/toast";
import { TrackingEmployeeParams } from "../../../../types/tracking";
import {
  addKeyInArray,
  selectArrayOption,
  selectStringOption,
} from "../../../../utils/utils";

const { Option } = Select;

interface EmployeeData {
  id: number;
  employee_id: string | null;
  name: string;
  department: string;
  designation: string;
  is_live_track_enable: 0 | 1;
}

export const employeeTrackingTabList = [
  {
    label: "Live Tracking",
    link: "/admin/settings/employee-tracking",
  },
  {
    label: "Live Tracking Change Log",
    link: "/admin/settings/employee-tracking/live-tracking-change-log",
  },
];

export const employeeTrackingTabListManager = [
  {
    label: "Live Tracking",
    link: "/admin/settings/employee-tracking",
  },
];

enum Status {
  active = "status-success",
  inactive = "status-danger",
}

export default function LiveTracking() {
  const [enableTracking, setEnableTracking] = useState(false);
  const [intervalDuration, setIntervalDuration] = useState<number>(15);
  const { authInfo } = useAppSelector((state) => state.auth);
  const [selectedData, setSelectedData] = useState<string[]>([]);
  const [showActionSelect, setShowActionSelect] = useState(true);
  const { query } = useQueryParams();
  const {
    page,
    limit,
    filter,
    keyword,
    handlePagination,
    handleLimit,
    handleFilter,
    handleSearch,
  } = useTable<TrackingEmployeeParams>({
    defaultFilter: {
      status:
        (Number(query.status) as TrackingEmployeeParams["status"]) || undefined,
    },
  });
  const { open, handleOpen, handleClose } = useModal<
    any,
    "CONFIRM" | "STATUS"
  >();
  const {
    open: bulkActionModalOpen,
    handleOpen: handleBulkActionModalOpen,
    handleClose: handleBulkActionModalClose,
  } = useModal<unknown, "CONFIRM_BULK_ACTION">();
  const { data: trackingSettingData } = useGetTrackingSettingQuery(
    authInfo?.business_id || null
  );

  const [
    updateTrackingSetting,
    {
      isLoading: isUpdateLoading,
      isSuccess: isUpdateSuccess,
      isError: isUpdateError,
      error: updateError,
    },
  ] = useUpdateTrackingSettingMutation();
  const {
    data: trackingEmployeeListData,
    isLoading: isTrackingEmployeeListDataLoading,
    isError: isTrackingEmployeeListError,
    error: trackingEmployeeListError,
  } = useGetTrackingEmployeeListQuery({
    params: {
      ...filter,
      search_keyword: keyword,
      manager_id: authInfo?.role === "Manager" ? authInfo?.id : undefined,
    },
  });
  const [
    addEmployeeLiveTrackingSetting,
    {
      isLoading: isAddEmployeeLiveTrackingSettingLoading,
      isSuccess: isAddEmployeeLiveTrackingSettingSuccess,
      isError: isAddEmployeeLiveTrackingSettingError,
      error: addEmployeeLiveTrackingSettingError,
    },
  ] = useAddEmployeeLiveTrackingSettingMutation();

  const {
    data: departmentsData,
    isError: isDepartmentsError,
    error: departmentsError,
  } = useGetDepartmentsQuery({
    params: { skip: 0, limit: 0, is_published: 1 },
  });

  useError(isUpdateError, updateError, "Error updating tracking setting");
  useError(isDepartmentsError, departmentsError, "Get departments data fail");
  useError(
    isAddEmployeeLiveTrackingSettingError,
    addEmployeeLiveTrackingSettingError,
    "Add live tracking setting fail"
  );
  useError(
    isTrackingEmployeeListError,
    trackingEmployeeListError,
    "Get employee list fail"
  );

  useEffect(() => {
    if (selectedData.length) {
      setShowActionSelect(true);
    } else {
      setShowActionSelect(false);
    }
  }, [selectedData]);

  // Table columns
  const columns = [
    {
      title: "Employee ID",
      dataIndex: "employee_id",
      sorter: (a: EmployeeData, b: EmployeeData) =>
        (a.employee_id || "").localeCompare(b.employee_id || ""),
      sortIcon: () => <SortIcon />,
      render: (val: string) => val || "n/a",
    },
    {
      title: "Employee Name",
      dataIndex: "name",
      sorter: (a: EmployeeData, b: EmployeeData) =>
        a.name.localeCompare(b.name),
      sortIcon: () => <SortIcon />,
    },
    {
      title: "Employee Email",
      dataIndex: "email",
    },
    {
      title: "Designation",
      dataIndex: "designation",
      render: (val: string) => val || "n/a",
    },
    {
      title: "Department",
      dataIndex: "department_name",
    },
    {
      title: "Status",
      dataIndex: "is_live_track_enable",
      key: "status",
      render: (status: number, record: any) =>
        authInfo?.role === "Admin" ? (
          <Switch
            checked={Boolean(status)}
            className="custom-switch"
            onChange={(checked) =>
              handleOpen({
                type: "STATUS",
                data: {
                  is_live_track_enable: checked ? 1 : 0,
                  employee: [record?.business_member_id],
                },
              })
            }
          />
        ) : (
          <span className={Status[status ? "active" : "inactive"]}>
            {status ? "Active" : "Inactive"}
          </span>
        ),
    },
  ];

  const showInfoText = (val: unknown) => {
    if (val === 0 || val === 1) {
      // this is for switch
      if (val) {
        return "Enabling this feature will let you live track your employee.";
      } else {
        return "If you disable this feature, you will not be able to live track your employee.";
      }
    } else {
      // this is for interval
      return "Are you sure to change tracking duration?";
    }
  };

  // const handleIntervalChange = (val: number | string) => {
  //   handleBulkActionModalOpen({ type: "CONFIRM_BULK_ACTION", data: val });
  // };

  const handleConfirmBtnClick = () => {
    updateTrackingSetting({
      id: authInfo?.business_id,
      data: {
        is_live_track_enable:
          typeof open.data === "boolean"
            ? open.data
              ? 1
              : 0
            : enableTracking
            ? 1
            : 0,
        interval_time:
          typeof open.data === "boolean" ? intervalDuration : open.data,
      },
    });
  };

  useEffect(() => {
    if (Object.keys(trackingSettingData?.data.tracking_settings || {}).length) {
      setEnableTracking(
        trackingSettingData?.data.tracking_settings?.is_tracking_enable
          ? true
          : false
      );
      setIntervalDuration(
        trackingSettingData?.data.tracking_settings
          .location_fetch_interval_in_minutes || 15
      );
    }
  }, [trackingSettingData]);

  useEffect(() => {
    if (isUpdateSuccess) {
      toast("success", "Live tracking updated successfully");
      handleClose();
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isUpdateSuccess]);

  useEffect(() => {
    if (isAddEmployeeLiveTrackingSettingSuccess) {
      toast("success", "Location tracking status successfully updated!");
      handleClose();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAddEmployeeLiveTrackingSettingSuccess]);

  useEffect(() => {
    if (
      isAddEmployeeLiveTrackingSettingSuccess ||
      isAddEmployeeLiveTrackingSettingError
    ) {
      handleBulkActionModalClose();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    isAddEmployeeLiveTrackingSettingSuccess,
    isAddEmployeeLiveTrackingSettingError,
  ]);

  const handleBulkActionConfirmBtnClick = () => {
    addEmployeeLiveTrackingSetting({
      employee: selectedData,
      is_live_track_enable: bulkActionModalOpen?.data === "Activate" ? 1 : 0,
    });
  };

  const handleStatusChange = () => {
    addEmployeeLiveTrackingSetting(open?.data);
  };

  return (
    <SidebarLayout
      title="Employee Tracking Setting"
      tabList={
        authInfo?.role === "Admin"
          ? employeeTrackingTabList
          : employeeTrackingTabListManager
      }
    >
      {open?.type === "CONFIRM" && (
        <ConfirmModal
          open={open?.open}
          handleOk={handleConfirmBtnClick}
          handleClose={handleClose}
          okText="Confirm"
          confirmTitle={"Confirmation"}
          questionText={`${showInfoText(open.data)}`}
          okLoading={isUpdateLoading}
        />
      )}
      {bulkActionModalOpen?.type === "CONFIRM_BULK_ACTION" && (
        <ConfirmModal
          open={bulkActionModalOpen?.open}
          handleOk={handleBulkActionConfirmBtnClick}
          handleClose={handleBulkActionModalClose}
          okText="Confirm"
          confirmTitle={"Confirmation"}
          questionText={`${
            bulkActionModalOpen?.data === "Activate"
              ? "Please acknowledge that the selected employee will fall under the live tracking criteria."
              : "Please acknowledge that the live location of certain employees cannot be tracked."
          }`}
          okLoading={isAddEmployeeLiveTrackingSettingLoading}
        />
      )}

      {open?.type === "STATUS" && (
        <ConfirmModal
          open={open?.open}
          handleOk={handleStatusChange}
          handleClose={handleClose}
          okText="Confirm"
          confirmTitle={"Confirmation"}
          questionText={`${
            open?.data?.is_live_track_enable
              ? "Please acknowledge that the selected employee will fall under the live tracking policy."
              : "Please acknowledge that the live location of certain employees cannot be tracked."
          }`}
          okLoading={isAddEmployeeLiveTrackingSettingLoading}
        />
      )}

      <div className="bg-white rounded-md p-4 pb-6 mb-4 flex flex-col">
        <div className="">
          <h2 className="text-[22px] font-semibold">Live Tracking</h2>
          <div className="flex items-center space-x-4 mt-4">
            <span className="text-sm font-medium">Live Tracking:</span>
            {authInfo?.role === "Admin" ? (
              <Switch
                onChange={(checked) =>
                  handleOpen({ type: "CONFIRM", data: checked })
                }
                checked={enableTracking}
              />
            ) : null}{" "}
            <span>
              {trackingSettingData?.data.tracking_settings?.is_tracking_enable
                ? "Activated"
                : "De-activated"}
            </span>
          </div>
        </div>
        <div className="mt-4 flex gap-2 items-center">
          <span className="text-sm font-medium">Tracking Duration:</span>
          <Select
            value={intervalDuration}
            onChange={(e) => handleOpen({ type: "CONFIRM", data: e })}
            className="w-40 mt-2"
            disabled={authInfo?.role !== "Admin"}
          >
            <Option value={15}>15 Minutes</Option>
            <Option value={30}>30 Minutes</Option>
            <Option value={60}>1 Hour</Option>
            <Option value={120}>2 Hour</Option>
          </Select>
        </div>
      </div>

      {trackingSettingData?.data.tracking_settings?.is_tracking_enable ? (
        <CustomTable
          loading={isTrackingEmployeeListDataLoading}
          limit={limit}
          page={page}
          columns={columns}
          total={trackingEmployeeListData?.data.total_employees}
          dataSource={addKeyInArray(trackingEmployeeListData?.data.employees)}
          // dataSource={addKeyInArray(employees)}
          handleLimit={handleLimit}
          handlePagination={handlePagination}
          rowSelection={
            authInfo?.role === "Admin"
              ? {
                  type: "checkbox",
                  onChange: (key, record) => {
                    setSelectedData(
                      record?.map(
                        (item) => item?.business_member_id
                      ) as string[]
                    );
                  },
                  columnWidth: 40,
                }
              : undefined
          }
          filterSection={
            <>
              {showActionSelect && (
                <Select
                  placeholder="Bulk Action"
                  filterOption={false}
                  options={selectStringOption(["Activate", "Deactivate"])}
                  className="min-w-[130px]"
                  suffixIcon={<SelectDownIcon />}
                  rootClassName="icon-gray"
                  onChange={(val) =>
                    handleBulkActionModalOpen({
                      type: "CONFIRM_BULK_ACTION",
                      data: val,
                    })
                  }
                />
              )}
              <Input
                suffix={<SearchIcon />}
                placeholder="Search"
                onChange={handleSearch}
              />
              <Select
                allowClear
                showSearch
                placeholder="Department"
                filterOption={false}
                options={selectArrayOption(
                  departmentsData?.data.departments || [],
                  "name",
                  "id"
                )}
                className="min-w-[130px]"
                suffixIcon={<SelectDownIcon />}
                rootClassName="icon-gray"
                onChange={(e) => handleFilter("department_id", e)}
              />
              <Select
                allowClear
                showSearch
                placeholder="Status"
                filterOption={false}
                options={[
                  { label: "Active", value: 1 },
                  { label: "Inactive", value: 0 },
                ]}
                className="min-w-[130px]"
                suffixIcon={<SelectDownIcon />}
                rootClassName="icon-gray"
                value={filter?.status}
                onChange={(e) => handleFilter("status", e)}
              />
            </>
          }
        />
      ) : null}
    </SidebarLayout>
  );
}
